import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, Spinner, StatusBadge } from "@/app/ui";
import { Clock, Play, Square, CheckCircle2 } from "lucide-react";

function TimekeepingInner() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "admin";
  const { data: active, refetch: refetchActive } = useFetch("/time-entries/active");
  const { data, loading, refetch } = useFetch("/time-entries");
  const { data: jobsData } = useFetch("/jobs");
  const jobs = jobsData?.items || [];
  const [jobId, setJobId] = useState("");

  const clockIn = async () => { try { await api.post("/time-entries/clock-in", { job_id: jobId || null }); toast.success("Clocked in"); setJobId(""); refetchActive(); refetch(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const clockOut = async () => { try { await api.post("/time-entries/clock-out"); toast.success("Clocked out"); refetchActive(); refetch(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const approve = async (id) => { await api.post(`/time-entries/${id}/approve`); toast.success("Approved"); refetch(); };

  const items = data?.items || [];
  const totalHours = items.reduce((s, t) => s + (t.hours || 0), 0);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Time Clock" description="Track hours worked and approve timesheets." />
      <div className="amden-card p-6 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`grid place-items-center h-12 w-12 rounded-xl ${active?.active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}><Clock size={24} /></span>
          <div>
            <p className="font-semibold text-navy">{active?.active ? "You're clocked in" : "You're clocked out"}</p>
            <p className="text-sm text-slate-500">{active?.active ? `Since ${new Date(active.active.clock_in).toLocaleTimeString()}` : "Tap to start tracking time"}</p>
          </div>
        </div>
        {active?.active ? (
          <Btn variant="danger" onClick={clockOut} data-testid="clock-out-button"><Square size={16} /> Clock out</Btn>
        ) : (
          <div className="flex items-center gap-2">
            <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="clock-job-select" className="rounded-lg border border-slate-300 px-3 py-2 bg-white text-sm">
              <option value="">General (no job)</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.number} · {j.title}</option>)}
            </select>
            <Btn variant="accent" onClick={clockIn} data-testid="clock-in-button"><Play size={16} /> Clock in</Btn>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-navy">Timesheet</h3>
        <span className="text-sm text-slate-500">Total: <span className="font-mono font-semibold">{totalHours.toFixed(2)}h</span></span>
      </div>
      {loading ? <Spinner /> : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              {isOwner && <th className="px-4 py-3 font-semibold">Employee</th>}
              <th className="px-4 py-3 font-semibold">Clock in</th><th className="px-4 py-3 font-semibold">Clock out</th>
              <th className="px-4 py-3 font-semibold text-right">Hours</th><th className="px-4 py-3 font-semibold">Status</th>{isOwner && <th></th>}</tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`time-entry-${t.id}`}>
                  {isOwner && <td className="px-4 py-3 text-navy font-medium">{t.employee_name}</td>}
                  <td className="px-4 py-3 text-slate-600">{new Date(t.clock_in).toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-600">{t.clock_out ? new Date(t.clock_out).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{t.hours?.toFixed(2) || "—"}</td>
                  <td className="px-4 py-3">{t.approved ? <StatusBadge status="approved" /> : <span className="text-xs text-slate-400">Pending</span>}</td>
                  {isOwner && <td className="px-4 py-3 text-right">{!t.approved && t.clock_out && <button onClick={() => approve(t.id)} className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-lg"><CheckCircle2 size={16} /></button>}</td>}
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No time entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Timekeeping() { return <PlanGate feature="timekeeping"><TimekeepingInner /></PlanGate>; }
